import React, { useState, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { useNavigate, useLocation } from "react-router-dom";
import { useTelegram } from "../../hooks/useTelegram";
import { motion } from "framer-motion";
import { useItemContext } from "../../contexts/ItemContext";
import {
	updateUser,
	getUserBalance,
	getGameLink,
	setGameLink,
	createInvoiceTBank,
	AddOrderBalance,
	getUserOrderApp,
	getUserPurchase,
	getFirstOrderByUserIdRateView,
	updateUserEmail,
} from "../../router/apiServiceFetch";

import { useFavs } from "../../contexts/FavsProvider";
import { useAuth } from "../../contexts/AuthProvider";
import Back from "../../assets/back.svg";
import Edit from "../../assets/edit.svg";
import Wallet from "../../assets/wallet.svg";
import RubleSvg from "../../assets/ruble.svg";
import userNoPic from "../../assets/user.png";
import Square from "../../assets/check-mark.png";
import Ruble from "../../assets/ruble.png";
import Load from "../../assets/load.gif";

import Heart from "../../assets/header/heart.svg";
import Support from "../../assets/header/support.svg";
import Comment from "../../assets/header/comment.png";
import Chat from "../../assets/chat.png";
import See from "../../assets/see.svg";
import Exit from "../../assets/exit.svg";

import "./Profile.scss";
import GameBannerSlider from "../../components/GameBannerSlider";
import Favorites from "../../components/Favorites/Favorites";
import Rates from "../../components/Rates/Rates";

const Profile = ({ onLoadingComplete }) => {
	const location = useLocation();

	const urlParams = new URLSearchParams(location.search);
	const viewRate = urlParams.get("viewRate");
	const [showUserRatesPopup, setShowUserRatesPopup] = useState(false);
	const [gameId, setGameId] = useState(null);
	const [orders, setOrders] = useState([]); // Store list of orders
	const [userEmail, setUserEmail] = useState(""); // Editable game link
	const [selectedPurchase, setSelectedPurchase] = useState(null); // Store selected purchase details
	const [isViewingPurchases, setIsViewingPurchases] = useState(false);
	const { favs, setFavs } = useFavs();
	const { handleAddToCart, handleRemoveFromCart, cartItems } = useItemContext();
	const { tg, triggerHapticFeedback, openChat } = useTelegram();
	const filePath = process.env.REACT_APP_FILE_PATH;
	const publicId = process.env.REACT_APP_PUBLIC_ID;
	const { userBase, setUserBase, userPic } = useAuth();
	const navigate = useNavigate();
	const [profilePic, setProfilePic] = useState(
		userBase.fileName && userBase.fileName.length > 0
			? `${filePath}/UserPhoto/${userBase.userId}/${userBase.fileName}`
			: userPic || userNoPic,
	);
	const [expandedOrderId, setExpandedOrderId] = useState(null);
	const [editedName, setEditedName] = useState(userBase.fullName);
	const [edit, setEdit] = useState(false);
	const [transActive, setTransActive] = useState(0);
	const [gameLinks, setGameLinks] = useState([]);
	const [balance, setBalance] = useState(0);
	const [isTopUp, setIsTopUp] = useState(false); // Состояние для отображения пополнения баланса
	const [topUpAmount, setTopUpAmount] = useState(""); // Введенная сумма
	const fileInputRef = useRef(null);

	const fetchUserOrders = async () => {
		try {
			const ordersData = await getUserOrderApp(userBase.userId); // Replace with your API call
			setOrders(ordersData);

			const result = await getFirstOrderByUserIdRateView(userBase.userId);
			if (result && result.gameId > 0 && viewRate == "1") {
				setGameId(result.gameId);
				setShowUserRatesPopup(true);
			}
		} catch (error) {
			console.error("Error fetching user orders:", error);
		} finally {
			if (onLoadingComplete) {
				onLoadingComplete();
			}
		}
	};
	useEffect(() => {
		setUserEmail((userBase?.email ?? "").trim());
	}, [userBase?.email]);

	const handleSaveEmail = async () => {
		const email = (userEmail ?? "").trim();
		const prevEmail = (userBase?.email ?? "").trim();

		// не дергаем API если ничего не поменялось
		if (email === prevEmail) return;

		// если хочешь запретить пустой email — оставь эту проверку
		if (!email) return;

		try {
			await updateUserEmail(userBase.userId, email);
			setUserBase((prev) => ({ ...prev, email }));
		} catch (error) {
			console.error("Error saving email:", error);
		}
	};

	const fetchPurchaseDetails = async (orderId) => {
		try {
			const purchaseDetails = await getUserPurchase(orderId); // Replace with your API call
			setSelectedPurchase(purchaseDetails);
			setIsViewingPurchases(true);
		} catch (error) {
			console.error("Error fetching purchase details:", error);
		}
	};

	const formatCurrency = (number) => {
		return new Intl.NumberFormat("ru-RU", {
			style: "currency",
			currency: "RUB",
			minimumFractionDigits: 2,
		}).format(number);
	};
	const formatCurrencyDisc = (number) => {
		return new Intl.NumberFormat("ru-RU", {
			currency: "RUB",
			minimumFractionDigits: 2,
		}).format(number);
	};
	const fetchUserBalance = async () => {
		try {
			const userBalance = await getUserBalance(userBase.userId);
			setBalance(Number(userBalance.balance) || 0);
		} catch (error) {
			console.error("Error fetching user balance:", error);
		}
	};

	const fetchUserGameLinks = async () => {
		try {
			const response = await getGameLink(userBase.userId);
			setGameLinks(
				response.map((link) => ({ ...link, editedLink: link.gameLink })),
			); // Add an editable field
		} catch (error) {
			console.error("Error fetching game links:", error);
		}
	};
	useEffect(() => {
		fetchUserGameLinks();
		fetchUserBalance();
		fetchUserOrders();
	}, [userBase.userId]);

	const handleOrderClick = (orderId) => {
		triggerHapticFeedback("impact", "medium");
		fetchPurchaseDetails(orderId);
	};

	const handleBackToOrders = () => {
		triggerHapticFeedback("impact", "medium");
		setIsViewingPurchases(false);
		setSelectedPurchase(null);
	};

	const handleTopUp = async () => {
		triggerHapticFeedback("impact", "medium");
		if (!topUpAmount || isNaN(topUpAmount) || Number(topUpAmount) < 50) {
			tg.showConfirm("Минимальная сумма пополнения - 50.00 ₽");
			return;
		}
		if (Number(topUpAmount) > 0) {
			const data = {
				orderId: 0,
				userId: userBase.userId,
				amount: topUpAmount,
				state: 4,
				superCell: null,
				code: null,
			};

			const orderId = await AddOrderBalance(data);
			const topUpAmountInKopecks = Math.round(topUpAmount * 100);
			const payData = {
				amount: topUpAmountInKopecks,
				orderId: orderId.toString(),
				description: `userId:${userBase.userId}, userName:${userBase.userName}`,
				type: 4,
			};
			const response = await createInvoiceTBank(payData);
			if (response && response.paymentURL) {
				setTopUpAmount(""); // Сбрасываем ввод
				setIsTopUp(false); // Возвращаемся к профилю
				tg.openLink(response.paymentURL);
			} else {
				console.error("Failed to retrieve payment URL.");
			}
			setTopUpAmount(""); // Сбрасываем ввод
			setIsTopUp(false); // Возвращаемся к профилю
		}
	};
	const handleOpenEdit = () => {
		triggerHapticFeedback("impact", "medium");
		setEdit(true);
	};

	const handleGoBack = () => {
		triggerHapticFeedback("impact", "medium");
		if (isTopUp) {
			setIsTopUp(false);
		} else if (edit) {
			setEdit(false);
		} else {
			navigate(-1);
		}
	};
	const handlePhotoChange = (event) => {
		if (edit) {
			const file = event.target.files[0];
			if (file) {
				const reader = new FileReader();
				reader.onloadend = () => {
					setProfilePic(reader.result);
				};
				reader.readAsDataURL(file);
			}
		}
	};

	const handlePhotoClick = () => {
		if (edit && fileInputRef.current) {
			triggerHapticFeedback("impact", "medium");
			fileInputRef.current.click(); // Programmatically trigger file input click
		}
	};

	const handleSave = async () => {
		triggerHapticFeedback("impact", "medium");
		try {
			// Check if profile picture or name has changed
			const photoBase64 = profilePic.startsWith("data:image")
				? profilePic.split(",")[1]
				: null;
			const generatedFileName = `profile_${userBase.userId}_${Date.now()}.jpg`;

			const hasProfileChanged =
				editedName !== userBase.fullName ||
				(profilePic.startsWith("data:image") && photoBase64);

			const hasGameLinksChanged = gameLinks.some(
				(link) => link.editedLink !== link.gameLink,
			);

			// Update user profile if changes exist
			if (hasProfileChanged) {
				await updateUser(
					userBase.userId,
					userBase.userName,
					editedName,
					photoBase64,
					generatedFileName,
				);
				setUserBase({
					...userBase,
					fullName: editedName,
					fileName: generatedFileName,
				});
			}

			// Update game links if changes exist
			if (hasGameLinksChanged) {
				for (const link of gameLinks) {
					if (link.editedLink !== link.gameLink) {
						await setGameLink(userBase.userId, link.gameId, link.editedLink);
					}
				}
				// Update local state after all API calls
				setGameLinks((prevLinks) =>
					prevLinks.map((prevLink) =>
						prevLink.editedLink !== prevLink.gameLink
							? { ...prevLink, gameLink: prevLink.editedLink }
							: prevLink,
					),
				);
			}
			await handleSaveEmail();

			// If no changes, do nothing
			if (!hasProfileChanged && !hasGameLinksChanged) {
				console.log("No changes to save.");
			}

			setEdit(false);
		} catch (error) {
			console.error("Error updating profile:", error);
		}
	};

	const handleGameLinkChange = (gameId, newLink) => {
		const updatedLinks = gameLinks.map((link) => {
			if (link.gameId === gameId) {
				return { ...link, editedLink: newLink };
			}
			return link;
		});
		setGameLinks(updatedLinks);
	};
	const handleOpenTopUp = () => {
		triggerHapticFeedback("impact", "medium");
		setIsTopUp(true);
	};
	const handleSupportClick = () => {
		triggerHapticFeedback("impact", "medium");
		openChat("nexushelper01"); // Replace with your actual Telegram username
	};
	const handleFavClick = () => {
		triggerHapticFeedback("impact", "medium");
		if (favs === 0) {
			setFavs(1);
		} else {
			setFavs(0);
		}
	};

	const toggleOrderDetails = async (orderId) => {
		triggerHapticFeedback("impact", "medium");
		if (expandedOrderId === orderId) {
			// Close the order if it's already expanded
			setExpandedOrderId(null);
			setSelectedPurchase([]);
		} else {
			// Open the order and fetch its details
			setExpandedOrderId(orderId);
			await fetchPurchaseDetails(orderId);
		}
	};
	return (
		<>
			{showUserRatesPopup && (
				<Rates onClose={() => setShowUserRatesPopup(false)} gameId={gameId} />
			)}
			{favs === 1 ? (
				<Favorites
					cartItems={cartItems}
					handleAddToCart={handleAddToCart}
					handleRemoveFromCart={handleRemoveFromCart}
				/>
			) : (
				<div className="profile">
					{edit && (
						<button className="save" onClick={handleSave}>
							<h2>Сохранить</h2>
						</button>
					)}
					<div className="back-nav">
						<div className="back-btn" onClick={handleGoBack}>
							<button>
								<img src={Back} alt="back" />
							</button>
						</div>

						<h2>{isTopUp ? "Пополнение баланса" : "Профиль"}</h2>
					</div>
					<div className="profile-container">
						{/* 
                <motion.div
                    initial={{ opacity: 0, y: isTopUp ? 50 : 0 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: isTopUp ? -50 : 0 }}
                    transition={{ duration: 0.4 }}
                    className="motion-container"
                > */}

						<motion.div
							initial={{ opacity: 0 }}
							animate={{
								opacity: !isTopUp ? 1 : 0,
								height: !isTopUp ? "auto" : 0,
							}}
							transition={{ duration: 0.3 }}
						>
							<div className="profile-content">
								<div className="profile-img" onClick={handlePhotoClick}>
									<img src={profilePic} alt="profile" className="tgImage" />

									{edit && <img src={Edit} alt="edit" className="editPic" />}

									<input
										type="file"
										ref={fileInputRef}
										style={{ display: "none" }} // Hide the file input
										accept="image/*"
										onChange={handlePhotoChange} // Handle file selection
									/>
								</div>
								<div className="profile-details">
									<div className="profile-name">
										<h2>
											{userBase.fullName.length > 15
												? userBase.fullName.substring(0, 15) + "..."
												: userBase.fullName}
										</h2>
										<h3>
											@
											{userBase.userName.length > 15
												? userBase.userName.substring(0, 15) + "..."
												: userBase.userName}
										</h3>
										<h3>
											{userBase.userId.length > 15
												? userBase.userId.substring(0, 15) + "..."
												: userBase.userId}
										</h3>
									</div>
									{!edit && (
										<div className="icons">
											<button className="icon-button">
												{/* <img src={Chat} alt="Logo" className="logo-image" /> */}
											</button>
											<button className="icon-button" onClick={handleFavClick}>
												<img src={Heart} alt="Logo" className="logo-image" />
											</button>

											<button className="icon-button" onClick={handleOpenEdit}>
												<img src={Edit} alt="edit" className="logo-image" />
											</button>
											<button
												className="icon-button"
												onClick={handleSupportClick}
											>
												<img src={Support} alt="Logo" className="logo-image" />
											</button>
										</div>
									)}
								</div>
							</div>

							<div className="border-div">
								<div className="border-div"></div>
							</div>
							{edit ? (
								<div className="profile-edit">
									<input
										type="text"
										placeholder="Имя Фамилия"
										value={editedName}
										onChange={(e) => setEditedName(e.target.value)}
										className="profile-input"
									></input>
									<h1>Почта для получения чеков покупок</h1>
									<div className="profile-edit-item" key={999}>
										<input
											id="userEmail"
											type="text"
											value={userEmail || ""}
											placeholder={"Введите вашу почту"}
											className="profile-edit-item-inner"
											onChange={(e) => setUserEmail(e.target.value)}
										/>
									</div>
									<div className="border-div">
										<div className="border-div"></div>
									</div>
									<h1>Подключите электронную почту к Supercell ID</h1>
									{gameLinks.map((item) => {
										return (
											<div className="profile-edit-item" key={item.gameId}>
												<input
													id={`gameLink-${item.gameId}`}
													type="text"
													value={item.editedLink || ""}
													placeholder={item.name}
													className="profile-edit-item-inner"
													onChange={(e) =>
														handleGameLinkChange(item.gameId, e.target.value)
													}
												/>
												<img
													src={`${filePath}/Games/${item.fileName}`}
													alt=""
													className="gameImg"
												/>
											</div>
										);
									})}
								</div>
							) : (
								<>
									<div className="profile-wallet">
										<div className="wallet-div">
											<div className="wallet-info">
												<p>
													<img src={Wallet} alt="wallet" className="wallet" />
													Баланс
												</p>
												<h1>{formatCurrency(balance)}</h1>
											</div>
											<img src={RubleSvg} alt="rubleSvg" className="rubleSvg" />
										</div>

										<div className="wallet-buttons">
											<button className="payBtn" onClick={handleOpenTopUp}>
												<h2>Пополнить Баланс</h2>
											</button>

											<button className="freeBtn">
												<h2>Получить бесплатный баланс</h2>
												{/* <h2>Оставить Чаевые</h2> */}
											</button>
										</div>

										<div className="border-div">
											<div className="border-div"></div>
										</div>
									</div>

									<div className="profile-trans">
										{orders.length > 0 ? (
											<table className="orders-table">
												<thead>
													<tr>
														<th>Код</th>
														<th>Дата</th>
														<th>Сумма</th>
														<th>Действия</th>
													</tr>
												</thead>
												<tbody>
													{orders.map((order) => (
														<React.Fragment key={order.orderId}>
															<tr>
																<td>{order.orderId}</td>
																<td>
																	{order.payDate
																		? new Date(
																				order.payDate,
																			).toLocaleDateString("ru-RU", {
																				day: "2-digit",
																				month: "2-digit",
																				year: "2-digit",
																			})
																		: ""}
																</td>
																<td>{formatCurrency(order.amount)}</td>
																<td className="action">
																	{order.state == 0 ||
																		order.state == 1 ||
																		order.state == 2 ||
																		(order.state == 3 && (
																			<button
																				onClick={() =>
																					toggleOrderDetails(order.orderId)
																				}
																				className="toggle-details-button"
																			>
																				<h2>
																					{expandedOrderId === order.orderId ? (
																						<img
																							src={Exit}
																							alt=""
																							className="see"
																						/>
																					) : (
																						<img
																							src={See}
																							alt=""
																							className="see"
																						/>
																					)}
																				</h2>
																			</button>
																		))}

																	{order.state == 1 ||
																		(order.state == 4 && (
																			<img
																				src={Load}
																				alt="load"
																				className="load"
																			/>
																		))}
																	{order.state == 3 && (
																		<img src={Square} alt="" className="load" />
																	)}
																	{order.state == 5 && (
																		<img src={Ruble} alt="" className="load" />
																	)}
																</td>
															</tr>
															{expandedOrderId === order.orderId && (
																<tr className="order-details-row">
																	<td colSpan="4" className="colSpan">
																		{selectedPurchase?.length > 0 ? (
																			<table className="purchase-details-table">
																				<thead>
																					<tr>
																						<th>Игра</th>
																						<th>Товар</th>
																						<th>Цена</th>
																					</tr>
																				</thead>
																				<tbody>
																					{selectedPurchase.map((item) => (
																						<tr key={item.id}>
																							<td className="state">
																								<div className="game-info">
																									<img
																										src={`${filePath}/Games/${item.parentFileName}`}
																										alt={item.parentName}
																										className="game-image"
																									/>
																								</div>
																							</td>
																							<td>
																								{item.count} x{" "}
																								<span>{item.itemName}</span>
																							</td>
																							<td>
																								{formatCurrency(item.price)}
																							</td>
																						</tr>
																					))}
																				</tbody>
																			</table>
																		) : (
																			<p className="nodata">Нет товаров</p>
																		)}
																	</td>
																</tr>
															)}
														</React.Fragment>
													))}
												</tbody>
											</table>
										) : (
											<p>У вас нет заказов.</p>
										)}
									</div>
								</>
							)}
						</motion.div>
						<motion.div
							initial={{ height: 0, opacity: 0 }}
							animate={{
								height: isTopUp ? "auto" : 0,
								opacity: isTopUp ? 1 : 0,
							}}
							transition={{ duration: 0.3 }}
						>
							<div className="profile-content isTop">
								<h3>Пополнить баланс в рублях</h3>
								<input
									type="number"
									value={topUpAmount}
									onChange={(e) => setTopUpAmount(e.target.value)}
									className="top-up-input"
									placeholder="Введите сумму для пополнения"
								/>
								<div className="box-content">
									<div className="box-content-inner">
										<div className="box-content-inner-in">
											{[100, 300, 500, 800, 1000, 1500, 3000, 5000, 10000].map(
												(amount) => (
													<button
														key={amount}
														className="amount-button"
														onClick={() => {
															setTopUpAmount(amount);
															triggerHapticFeedback("impact", "medium");
														}}
													>
														+{formatCurrencyDisc(amount)}
													</button>
												),
											)}
										</div>
									</div>
								</div>
								<div className="buy-div">
									<button onClick={handleTopUp} className="top-up-button">
										Пополнить
									</button>
								</div>
							</div>
						</motion.div>
					</div>
				</div>
			)}
		</>
	);
};

export default Profile;
